// "Place <entry>" (issue 1045, option C): the DM chooses the name the players will see — the entry's own by default, or
// "Creature N" at a press — whether it stands hidden, and whether it joins the turn order, then places it. Opened by a
// compendium result dropped on the board (at the point under the hand), dropped on the turn tracker (the turn order
// ticked; at the board's centre of view, the first free square near it) or by a result's Place button (the same).
// Enter places. The server does the placing; a refusal is said in the dialog, in its own words.
// Issue 1054: when the table's system has templates (D&D 3.5; Pathfinder none until #822), an optional Template picker
// (search, then pick) previews the numbers the server will place — the same `applyTemplate` — and the changes in words,
// with the template's prose shown and not applied; the name becomes "<Template> <Monster>" unless the DM typed one.
// Issue 1056: a campaign NPC (the Campaign NPCs tool) opens the same dialog with `npc` named: hidden ticked, no template
// picker, placed by the NPC's id so the server reads its stat block.
// Issue 1584: an entry of a marker family (a trap) opens with Hidden ticked and neither the turn order nor the template
// picker: it is placed as a hidden marker linked to its entry.
import { useEffect, useMemo, useState } from 'react';
import { EMPTY_GRID, applyTemplate, boardExtent, formatCr, parseCr, templatedName, type MonsterNumbers, type Point } from '@dragons-table/vtt-shared';
import { Modal } from '../Dialogs';
import { useShell } from '../context';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { useNumberWords } from '../../table/numberWords';
import { useBoardMap } from '../../board/sceneMap';
import { boardBus, type EntryDrop } from '../../board/bus';
import { placeNpc } from '../../npcs';
import { nextCreatureName, placeEntry, placeKinds, placeSpot, templateChoices, viewCentreOr, type DraggedEntry, type TemplateChoice } from '../../place';
import { BTN_MD, PRIMARY } from '../ui';
import { track } from '../../analytics';

const btn = BTN_MD;
const UNKNOWN: MonsterNumbers = { hp: null, ac: null, hitDice: null, dex: null, con: null, cr: null };
const SEARCH_MS = 250;
const crText = (v: MonsterNumbers['cr']): string | null => { const n = parseCr(v); return n === null ? null : formatCr(n); };
const was = (label: string, from: string | null, to: string | null) => (from === to ? `${label} ${to ?? '?'}` : `${label} ${from ?? '?'} → ${to ?? '?'}`);

const primary = PRIMARY;

export interface PlaceRequest {
  entry: DraggedEntry; at: Point | null; tracker: boolean;
  /** Issue 1056: a campaign NPC, placed by its id (its stat block is the server's to read); hidden by default, no templates. */
  npc?: { id: string };
}

export function PlaceDialog({ entry, at, tracker: trackerAtFirst, npc, onClose }: PlaceRequest & { onClose: () => void }) {
  const { campaignId } = useShell();
  const { store, scene, setNotice } = useTableState();
  const words = useNumberWords();
  const map = useBoardMap();
  const [name, setName] = useState(entry.name.slice(0, 120));
  const [hidden, setHidden] = useState(!!npc);
  const [tracker, setTracker] = useState(trackerAtFirst);
  const [said, setSaid] = useState('');
  const [busy, setBusy] = useState(false);
  // Issue 1054: the templates on offer (none: no picker), the search, the one chosen and the monster's own numbers.
  const [offered, setOffered] = useState(false);
  const [templateQuery, setTemplateQuery] = useState('');
  const [found, setFound] = useState<TemplateChoice[]>([]);
  const [chosen, setChosen] = useState<TemplateChoice | null>(null);
  const [base, setBase] = useState<MonsterNumbers | null>(null);
  const [marker, setMarker] = useState(false);
  useEffect(() => {
    if (npc) return undefined;
    let live = true;
    placeKinds(campaignId).then((k) => {
      if (!live || !k.markers.includes(entry.segment)) return;
      setMarker(true); setHidden(true); setTracker(false); setChosen(null);
    }).catch(() => undefined);
    return () => { live = false; };
  }, [campaignId, entry.segment]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (npc) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      templateChoices(campaignId, templateQuery, entry).then((r) => {
        if (!live) return;
        setFound(r.templates);
        if (r.base) setBase(r.base);
        if (r.templates.length) setOffered(true);
      }).catch(() => undefined);
    }, templateQuery ? SEARCH_MS : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [campaignId, entry.segment, entry.slug, templateQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  const own = entry.name.slice(0, 120);
  const pick = (slug: string) => {
    const next = slug ? (found.find((t) => t.slug === slug) ?? (chosen?.slug === slug ? chosen : null)) : null;
    // The name follows the template only while it is the one the dialog gave it.
    const given = chosen ? templatedName(chosen.name, own) : own;
    if (name.trim() === given) setName(next ? templatedName(next.name, own) : own);
    setChosen(next);
    setSaid('');
  };
  const preview = chosen ? applyTemplate(base ?? UNKNOWN, chosen.adjust) : null;
  const options = chosen && !found.some((t) => t.slug === chosen.slug) ? [chosen, ...found] : found;
  const objects = Object.values(store.state.objects);
  const creature = useMemo(() => nextCreatureName([
    ...objects.map((o) => o.name ?? ''),
    ...((store.state.encounter?.combatants ?? []) as Array<{ name?: string }>).map((c) => c.name ?? ''),
  ]), [store.state.objects, store.state.encounter]); // eslint-disable-line react-hooks/exhaustive-deps

  const place = async () => {
    const shown = name.trim();
    if (!shown) { setSaid('Give it a name the players will see.'); return; }
    const extent = boardExtent(map);
    const point = at ?? viewCentreOr({ x: EMPTY_GRID.squarePx * 2.5, y: EMPTY_GRID.squarePx * 2.5 });
    const spot = placeSpot(store.state.grid, extent, point, store.state.scene.snap !== false, objects.map((o) => ({ x: o.x, y: o.y })), at === null);
    setBusy(true);
    const r = npc
      ? await placeNpc(campaignId, npc.id, { x: spot.x, y: spot.y, name: shown, hidden, tracker }, scene?.id ?? null)
      : await placeEntry(campaignId, { segment: entry.segment, slug: entry.slug, x: spot.x, y: spot.y, name: shown, hidden, tracker, ...(chosen ? { template: chosen.slug } : {}) }, scene?.id ?? null);
    setBusy(false);
    if (r.kind === 'ok') {
      if (marker) track('trap-place'); else track('token-place', { from: npc ? 'npc' : 'compendium' });
      // The name it took (qa-runs#362: numbered as it is placed, "Goblin 1").
      setNotice(`Placed ${('name' in r && r.name) || shown}${hidden ? ', hidden' : ''}${tracker ? ', in the turn order' : ''}.`);
      onClose();
      return;
    }
    setSaid(r.kind === 'refused' ? r.reason : "The table can't be reached just now. Try again in a moment.");
  };

  return (
    <Modal title={`Place ${entry.name}`} onClose={onClose} testId="place-entry">
      <form className="space-y-3 text-sm" onSubmit={(e) => { e.preventDefault(); if (!busy) void place(); }}>
        <div className="space-y-1">
          <label htmlFor="place-name" className="block text-stone-300">Name shown to players</label>
          <div className="flex gap-2">
            <input id="place-name" data-testid="place-name" className="min-w-0 flex-1 rounded bg-stone-800 px-2 py-1" value={name} maxLength={120} onChange={(e) => { setName(e.target.value); setSaid(''); }} />
            <button type="button" className={btn} data-testid="place-creature" title="A name that gives nothing away" onClick={() => { setName(creature); setSaid(''); }}>{creature}</button>
          </div>
        </div>
        {offered && !marker && (
          <fieldset className="space-y-1" data-testid="place-template-picker">
            <legend className="text-stone-300">Template (optional)</legend>
            <div className="flex gap-2">
              <input type="search" aria-label="Search templates" data-testid="place-template-search" placeholder="Half-fiend, celestial…" maxLength={100}
                className="min-w-0 flex-1 rounded bg-stone-800 px-2 py-1" value={templateQuery} onChange={(e) => setTemplateQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
              <select aria-label="Template" data-testid="place-template" className="min-w-0 flex-1 rounded bg-stone-800 px-1 py-1" value={chosen?.slug ?? ''} onChange={(e) => pick(e.target.value)}>
                <option value="">No template</option>
                {options.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
              </select>
            </div>
            {chosen && preview && (
              <div data-testid="place-template-preview" className="space-y-1 rounded border border-stone-700 p-2 text-xs">
                <p data-testid="place-template-numbers" className="font-medium text-stone-200">
                  {[
                    was(words.health, base?.hp != null ? String(base.hp) : null, preview.hp !== null ? String(preview.hp) : null),
                    was(words.defence, base?.ac != null ? String(base.ac) : null, preview.ac !== null ? String(preview.ac) : null),
                    was(words.challenge, crText(base?.cr ?? null), preview.cr !== null ? formatCr(preview.cr) : null),
                  ].join(' · ')}
                </p>
                <ul className="list-disc pl-4 text-stone-300">{preview.changes.map((c) => <li key={c}>{c}</li>)}</ul>
                {chosen.notes.length > 0 && (
                  <dl className="space-y-1 text-stone-400">
                    {chosen.notes.map((n) => <div key={n.label}><dt className="inline font-medium">{n.label}: </dt><dd className="inline">{n.text}</dd></div>)}
                  </dl>
                )}
                <p className="text-stone-500">The prose is shown, not applied; the token links to the {entry.name} entry.</p>
              </div>
            )}
          </fieldset>
        )}
        <label className="flex items-center gap-2"><input type="checkbox" data-testid="place-hidden" checked={hidden} onChange={(e) => setHidden(e.target.checked)} /> Hidden</label>
        {marker
          ? <p className="text-xs text-stone-400" data-testid="place-marker">A trap: placed as a marker linked to its entry, in no turn order. Its DCs show in Properties.</p>
          : <label className="flex items-center gap-2"><input type="checkbox" data-testid="place-tracker" checked={tracker} onChange={(e) => setTracker(e.target.checked)} /> Also add to the turn order</label>}
        <p className="text-xs text-stone-500">It comes as a plain disc: the site has no picture for it yet.</p>
        {said && <p role="alert" className="text-amber-300" data-testid="place-said">{said}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} data-testid="place-cancel" onClick={onClose}>Cancel</button>
          <button type="submit" className={primary} data-testid="place-submit" disabled={busy}>Place</button>
        </div>
      </form>
    </Modal>
  );
}

/** The board's half (mounted on the board page): a result dropped on the board opens the dialog at the point under the hand. */
export function PlaceGate() {
  const { viewingAs } = useTableState();
  const mayAdd = useMayAct()('object.add');
  const [asked, setAsked] = useState<EntryDrop | null>(null);
  useEffect(() => boardBus.onEntryDropped((d) => { if (mayAdd && !viewingAs) setAsked(d); }), [mayAdd, viewingAs]);
  if (!asked) return null;
  return <PlaceDialog entry={asked.entry} at={asked.at} tracker={false} onClose={() => setAsked(null)} />;
}
